import { useModal } from "@surveying-hub-bv/fe-component-library"
import { ReactNode, useState } from "react"
import { AlertIcon } from "./ContentElements"
import { Button } from "./Buttons"
import { useTranslation } from "react-i18next"
import EagerSVGIcon from "./EagerSVG"
import { PhoneNumber, TextInput } from "./Forms"
import { getGclid } from "../utils/getGoogleClickID"

export const SomethingWentWrong = () => {
    const { hideModal } = useModal()

    return <div className="flex flex-col gap-4">
        Something went unexpectetly wrong.

        <Button label="Close" onClick={hideModal} />
    </div>
}

export const Loading = ({ text }: { text: string }) => {
    return <div className="flex flex-col gap-4">
        {text}
        <div className=" w-[225px] h-[225px]">
            <div className="loader" />
        </div>
    </div>
}


export const GenericErrorDialog = () => {
    const { hideModal } = useModal()

    return (
        <div className="modal-content">
            <AlertIcon type="error" />
            <h3 className="modal-title">An Unexpected Error Occurred</h3>
            <p className="dark:text-darkMode-secondaryText text-center">The application ran into an unexpected error processing your last request. Please refresh the page and try again.</p>

            <div className="modal-footer-actions">
                <Button label="Close" onClick={hideModal} buttonStyle="muted" alwaysFullWidth size="large" />
            </div>
        </div>
    )
}

export const GenericNoBillingDetailsDialog = () => {
    const { hideModal } = useModal()

    return (
        <div className="modal-content">
            <AlertIcon type="info" />
            <h3 className="modal-title text-center">No Billing History</h3>
            <p className="dark:text-darkMode-secondaryText text-center mb-4">We couldn't find any active subscriptions or billing history for your account.</p>

            <Button label="Close" onClick={hideModal} buttonStyle="muted" size="large" alwaysFullWidth />
        </div>
    )
}

// Rebranded CTA Dialog -- Step 1 of 2
type RebrandedCTADialogProps = {
    loggedInEmail: string
    submittedFrom: string
}
export const RebrandedCTADialog = ({ loggedInEmail, submittedFrom }: RebrandedCTADialogProps) => {
    const { t } = useTranslation()
    const { showModal, hideModal } = useModal()

    if (document.cookie.includes("rebrandedFormSubmitted=true")) {
        return (
            <div className="modal-content">
                <AlertIcon type="info" />
                <h3 className="modal-title text-center">Thank you!</h3>
                <p className="dark:text-darkMode-secondaryText text-center">Your request has been submitted. Our team will reach out soon.</p>
                <Button label="Close" onClick={hideModal} buttonStyle="muted" size="large" alwaysFullWidth />
            </div>
        )
    } else {
        return (
            <div className="modal-content">
                <p className="uppercase dark:text-darkMode-mutedText -mb-2">Step 1 of 2</p>
                <div>
                    <h3 className="modal-title mb-1">Launch Your Branded <span className="dark:text-darkMode-primary">RTK Platform</span></h3>
                    <p className="dark:text-darkMode-secondaryText mb-2">No servers, no coding and your own domain name.</p>
                </div>

                <Button
                    label="Request a Demo"
                    onClick={() => showModal(<RebrandedFormDialog loggedInEmail={loggedInEmail} submittedFrom={submittedFrom} />)}
                    buttonStyle="secondary"
                    buttonCustomClasses="mx-auto mb-2"
                    alwaysFitWidth
                />

                <div className="flex flex-col gap-y-3">
                    <div className="flex items-start gap-x-2 text-[18px]">
                        <EagerSVGIcon name="SuccessIcon" width={30} height={30} className="dark:text-darkMode-primary flex-shrink-0" />
                        <div className="text-left">
                            Sell RTK Access on Autopilot*
                            <p className="text-[14px] dark:text-darkMode-mutedText">Manage and automate paid access to every GNSS network. </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-x-2 text-[18px]">
                        <EagerSVGIcon name="SuccessIcon" width={30} height={30} className="dark:text-darkMode-primary flex-shrink-0" />
                        <div className="text-left">
                            Fleet Access Management
                            <p className="text-[14px] dark:text-darkMode-mutedText">Control who connects to what and scale easily with full visibility.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-x-2 text-[18px]">
                        <EagerSVGIcon name="SuccessIcon" width={30} height={30} className="dark:text-darkMode-primary flex-shrink-0" />
                        <div className="text-left">
                            Build Your RTK Network
                            <p className="text-[14px] dark:text-darkMode-mutedText">Set up and oversee your own correction networks with ease.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-x-2 text-[18px]">
                        <EagerSVGIcon name="SuccessIcon" width={30} height={30} className="dark:text-darkMode-primary flex-shrink-0" />
                        <div className="text-left">
                            Manage 3rd-party Subscriptions
                            <p className="text-[14px] dark:text-darkMode-mutedText">Already have RTK subscriptions? Manage them in the same portal.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-x-2 text-[18px]">
                        <EagerSVGIcon name="SuccessIcon" width={30} height={30} className="dark:text-darkMode-primary flex-shrink-0" />
                        <div className="text-left">
                            Integrate RTK with API
                            <p className="text-[14px] dark:text-darkMode-mutedText">Integrate any RTK network in your solution through one API gateway.</p>
                        </div>
                    </div>
                </div>
                <p className="text-[14px] dark:text-darkMode-mutedText italic">*Verify with third party network if selling and charging for paid access is permitted.</p>

                <div className="flex gap-1.5 items-center">
                    <EagerSVGIcon name="ApprovedIcon" width={20} height={20} className="dark:text-darkMode-primary flex-shrink-0" />
                    <p className="break-words">Trusted by 200+ fleets in agri-tech, drone & construction.</p>
                </div>

                <div className="modal-footer-actions">
                    <Button label={t("common.close")} onClick={hideModal} buttonStyle="muted" size="large" />
                    <Button
                        label="Request a Demo"
                        onClick={() => showModal(<RebrandedFormDialog loggedInEmail={loggedInEmail} submittedFrom={submittedFrom} />)}
                        buttonStyle="primary"
                        size="large"
                    />
                </div>
            </div>
        )
    }
}




// Rebranded Form Dialog -- Step 2 of 2
type RebrandedFormDialogProps = {
    loggedInEmail: string
    submittedFrom: string
}
export const RebrandedFormDialog = ({ loggedInEmail, submittedFrom }: RebrandedFormDialogProps) => {
    const { showModal, hideModal } = useModal()

    // Forms
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [company, setCompany] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [message, setMessage] = useState("")
    const MESSAGE_LIMIT = 200

    // Validation
    const isEmailValid = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    const isPhoneValid = (phone: string) =>
        phone.length === 0 || /^[\d+\-\s()]+$/.test(phone)

    const isFormValid =
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        email.trim().length > 0 &&
        isEmailValid(email) &&
        isPhoneValid(phoneNumber)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isFormValid) return
        setSubmitting(true)
        const payload = {
            firstName,
            lastName,
            email,
            loggedInEmail,
            company,
            phoneNumber,
            submittedFrom,
            message,
            gclid: getGclid(),
        }

        try {
            const response = await fetch("https://surveyinghub.app.n8n.cloud/webhook/76abe576-dfa0-486a-b9d9-36fb21e1b570", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                showModal(
                    <div className="modal-content">
                        <AlertIcon type="error" />
                        <h3 className="modal-title text-center">Submission Failed</h3>
                        <p className="dark:text-darkMode-secondaryText text-center">
                            Sorry, something went wrong while submitting your request. Please try again later.
                        </p>
                        <Button label="Close" onClick={hideModal} size="large" alwaysFullWidth />
                    </div>
                )
            }

            // Show confirmation dialog or reset form as needed
            showModal(
                <div className="modal-content">
                    <AlertIcon type="success" />
                    <h3 className="modal-title text-center">Thank you!</h3>
                    <p className="dark:text-darkMode-secondaryText text-center">
                        Your request has been submitted. Our team will reach out soon.
                    </p>
                    <Button label="Close" onClick={hideModal} size="large" alwaysFullWidth />
                </div>
            )

            // Set cookie for rebranded form submission to avoid resubmission spamming.
            document.cookie = `rebrandedFormSubmitted=true; path=/; max-age=86400;` // 1 day expiration
        } catch (error) {
            showModal(
                <div className="modal-content">
                    <AlertIcon type="error" />
                    <h3 className="modal-title text-center">Submission Failed</h3>
                    <p className="dark:text-darkMode-secondaryText text-center">
                        Sorry, something went wrong while submitting your request. Please try again later.
                    </p>
                    <Button label="Close" onClick={hideModal} size="large" alwaysFullWidth />
                </div>
            )
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="modal-content">
            <p className="uppercase dark:text-darkMode-mutedText -mb-2">Step 2 of 2</p>
            <div>
                <h3 className="modal-title">Contact Sales</h3>
                <p className="dark:text-darkMode-secondaryText mb-2">Complete the form below and we'll get back to you.</p>
            </div>

            <form className="flex flex-col gap-y-3" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-3">
                    <TextInput
                        label="First name"
                        type="text"
                        name="fname"
                        placeholder="e.g. John"
                        autoComplete="on"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />
                    <TextInput
                        label="Last name"
                        type="text"
                        name="lname"
                        placeholder="e.g. Doe"
                        autoComplete="on"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                    />
                </div>

                <TextInput
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="john.doe@domain.com"
                    autoComplete="on"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <PhoneNumber
                    label="Phone number"
                    value={phoneNumber}
                    required
                    setValue={setPhoneNumber}
                />

                <TextInput
                    label="Company (optional)"
                    type="text"
                    name="company"
                    placeholder="Enter your company name"
                    autoComplete="on"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                />

                <div className="flex flex-col">
                    <label className="text-[14px] dark:text-white text-left">Message (optional):</label>
                    <textarea
                        className="dark:bg-darkMode-formBg transition-all duration-300 dark:placeholder:text-darkMode-mutedText  px-3 py-3 border dark:border-darkMode-layer3border dark:focus:border-white rounded-sm "
                        placeholder="Enter your message"
                        value={message}
                        onChange={(e) => {
                            if (e.target.value.length <= MESSAGE_LIMIT) setMessage(e.target.value)
                        }}
                        rows={2}
                        maxLength={MESSAGE_LIMIT}
                    />
                    <span className={`text-xs mt-1 text-right ${message.length >= MESSAGE_LIMIT ? "text-red-500" : "text-gray-500 dark:text-darkMode-mutedText"}`}>
                        {message.length}/{MESSAGE_LIMIT}
                    </span>
                </div>

                <p className="dark:text-darkMode-secondaryText">Our team will reach out soon to answer any questions you may have.</p>

                <div className="modal-footer-actions">
                    <Button
                        label="Back"
                        onClick={() => showModal(<RebrandedCTADialog loggedInEmail={loggedInEmail} submittedFrom={submittedFrom} />)}
                        buttonStyle="muted"
                        size="large"
                        type="button"
                        disabled={submitting}
                    />
                    <Button
                        label={submitting ? "Submitting..." : "Submit"}
                        onClick={() => handleSubmit}
                        buttonStyle="primary"
                        size="large"
                        type="submit"
                        disabled={submitting || !isFormValid}
                    />
                </div>
            </form>
        </div>
    )
}


// Legacy code, should be removed in the future.
export const DangerZone = ({ name, removeAction }: { name: string, removeAction: () => Promise<void> }) => {
    const { showModal, hideModal } = useModal()
    const [showDangerZone, setShowDangerZone] = useState(false);
    const removeAccount = async () => {
        let shouldOpen = true
        showModal(<Loading text={`Deleting ${name}`} />, () => shouldOpen = false)
        await removeAction()
        if (shouldOpen) showModal(<div className="flex flex-col gap-4">
            {name} was deleted.
            <Button label="Close" onClick={hideModal} />
        </div>)
    }
    return (
        <div className="mt-5">
            <div className="pt-4">
                <button
                    className="text-red-600 hover:text-red-800 focus:outline-none"
                    onClick={() => setShowDangerZone(!showDangerZone)}>
                    {showDangerZone ? 'Hide Danger Zone' : 'Show Danger Zone'}
                </button>
            </div>
            {showDangerZone && (
                <div className="mt-3 flex justify-center items-center gap-x-4">
                    <Button label={"Delete"} buttonStyle='danger' onClick={removeAccount} />
                </div>
            )}
        </div>
    );
}

export async function loadingAction<T, E>(
    showModal: (content: ReactNode, onClose?: () => void) => void,
    text: string,
    promise: Promise<T>,
    finished: (result: T) => ReactNode,
    onError: (result: E) => ReactNode,
    onClose?: (result: T) => void): Promise<void> {
    let shouldOpen = true
    showModal(<Loading text={text} />, () => shouldOpen = false)
    return promise.then(result => { if (shouldOpen) showModal(finished(result), () => onClose && onClose(result)) })
        .catch(error => { if (shouldOpen) { showModal(onError(error)) } })
}